import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useGraphEditorStore } from '@/graph-store';
import { useToast } from '@/hooks/useToast';
import type { CycleRange } from '@/graph/domain/types';

export interface EditGroupModalProps {
  groupId: string;
  onClose: () => void;
}

export function EditGroupModal({ groupId, onClose }: EditGroupModalProps) {
  const group = useGraphEditorStore((s) => s.graphData.groups[groupId]);
  const operationNodes = useGraphEditorStore((s) => s.graphData.operationNodes);
  const updateGroup = useGraphEditorStore((s) => s.updateGroup);
  const addCycle = useGraphEditorStore((s) => s.addCycle);
  const editCycle = useGraphEditorStore((s) => s.editCycle);
  const removeCycle = useGraphEditorStore((s) => s.removeCycle);
  const removeStepsFromGroup = useGraphEditorStore((s) => s.removeStepsFromGroup);
  const { showError } = useToast();

  const [label, setLabel] = useState(group?.label ?? '');
  const [notes, setNotes] = useState(group?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  // New-cycle form state
  const [showCycleForm, setShowCycleForm] = useState(false);
  const [cycleStart, setCycleStart] = useState(0);
  const [cycleEnd, setCycleEnd] = useState(0);
  const [cycleIterations, setCycleIterations] = useState(2);
  const [cycleLabel, setCycleLabel] = useState('');

  const [draftCycles, setDraftCycles] = useState<CycleRange[]>(group?.cycleRanges ?? []);

  useEffect(() => {
    if (!group) return;
    setDraftCycles(group.cycleRanges ?? []);
  }, [group]);

  const existingCycleIds = useMemo(
    () => new Set((group?.cycleRanges ?? []).map((cycle) => cycle.id)),
    [group],
  );

  if (!group) return null;

  const stepDisplayNames = group.stepIds.map(
    (id) => operationNodes[id]?.displayName ?? id,
  );

  const handleSave = () => {
    setError(null);
    try {
      updateGroup(groupId, {
        label: label.trim() || group.label,
        notes: notes || undefined,
      });

      const originalCycles = group.cycleRanges ?? [];
      const originalCycleMap = new Map(originalCycles.map((cycle) => [cycle.id, cycle]));

      for (const cycle of originalCycles) {
        if (!draftCycles.some((draftCycle) => draftCycle.id === cycle.id)) {
          removeCycle(groupId, cycle.id);
        }
      }

      for (const cycle of draftCycles) {
        if (!existingCycleIds.has(cycle.id)) {
          addCycle(groupId, {
            startIndex: cycle.startIndex,
            endIndex: cycle.endIndex,
            iterationCount: cycle.iterationCount,
            label: cycle.label,
          });
          continue;
        }

        const original = originalCycleMap.get(cycle.id);
        if (!original) continue;

        if (
          original.startIndex !== cycle.startIndex ||
          original.endIndex !== cycle.endIndex ||
          original.iterationCount !== cycle.iterationCount ||
          original.label !== cycle.label
        ) {
          editCycle(groupId, cycle.id, {
            startIndex: cycle.startIndex,
            endIndex: cycle.endIndex,
            iterationCount: cycle.iterationCount,
            label: cycle.label,
          });
        }
      }

      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      showError(errorMessage);
      setError(errorMessage);
    }
  };

  const handleRemoveStep = (stepId: string) => {
    setError(null);
    try {
      removeStepsFromGroup(groupId, [stepId]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      showError(errorMessage);
      setError(errorMessage);
    }
  };

  const handleAddCycle = () => {
    setError(null);
    if (cycleStart > cycleEnd) {
      const errorMessage = 'Start step must come before end step.';
      showError(errorMessage);
      setError(errorMessage);
      return;
    }
    const newCycle: CycleRange = {
      id: `draft-cycle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        startIndex: cycleStart,
        endIndex: cycleEnd,
        iterationCount: cycleIterations,
        label: cycleLabel || undefined,
      };

      setDraftCycles((prev) => [...prev, newCycle]);
      setShowCycleForm(false);
      setCycleStart(0);
      setCycleEnd(0);
      setCycleIterations(2);
      setCycleLabel('');
  };

  const handleRemoveCycle = (cycleId: string) => {
    setError(null);
    setDraftCycles((prev) => prev.filter((cycle) => cycle.id !== cycleId));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Edit Group
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            required
            size="small"
            fullWidth
            placeholder="(Enter group name)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoFocus
          />
          <TextField
            label="Notes"
            multiline
            rows={2}
            size="small"
            fullWidth
            placeholder="Enter Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {/* Members list */}
          <Typography variant="subtitle2">
            Members ({group.stepIds.length})
          </Typography>
          <List dense disablePadding>
            {group.stepIds.map((id, idx) => (
              <ListItem
                key={id}
                secondaryAction={
                  group.stepIds.length > 1 ? (
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => handleRemoveStep(id)}
                      aria-label="remove step"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : null
                }
              >
                <ListItemText
                  primary={`${idx + 1}. ${stepDisplayNames[idx]}`}
                />
              </ListItem>
            ))}
          </List>

          <Divider />

          {/* Cycles section */}
          <Typography variant="subtitle2">
            Cycles ({draftCycles.length})
          </Typography>
          {draftCycles.map((cycle) => {
            const startName = operationNodes[group.stepIds[cycle.startIndex]]?.displayName ?? `Step ${cycle.startIndex + 1}`;
            const endName = operationNodes[group.stepIds[cycle.endIndex]]?.displayName ?? `Step ${cycle.endIndex + 1}`;
            return (
            <Box
              key={cycle.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pl: 1,
              }}
            >
              <Chip
                size="small"
                label={`${startName} → ${endName} × ${cycle.iterationCount}`}
              />
              {cycle.label && (
                <Typography variant="caption" color="text.secondary">
                  {cycle.label}
                </Typography>
              )}
              <IconButton
                size="small"
                onClick={() => handleRemoveCycle(cycle.id)}
                aria-label="remove cycle"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            );
          })}

          {!showCycleForm && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowCycleForm(true)}
            >
              Add Cycle
            </Button>
          )}

          {showCycleForm && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                New cycle
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  select
                  label="Start step"
                  size="small"
                  value={cycleStart}
                  onChange={(e) => setCycleStart(Number(e.target.value))}
                  sx={{ flex: 1 }}
                >
                  {group.stepIds.map((opId, idx) => (
                    <MenuItem key={opId} value={idx}>
                      {idx + 1}. {operationNodes[opId]?.displayName ?? opId}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="End step"
                  size="small"
                  value={cycleEnd}
                  onChange={(e) => setCycleEnd(Number(e.target.value))}
                  sx={{ flex: 1 }}
                >
                  {group.stepIds.map((opId, idx) => (
                    <MenuItem key={opId} value={idx}>
                      {idx + 1}. {operationNodes[opId]?.displayName ?? opId}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Iterations"
                  type="number"
                  size="small"
                  value={cycleIterations}
                  onChange={(e) => setCycleIterations(Number(e.target.value))}
                  slotProps={{ htmlInput: { min: 2 } }}
                  sx={{ width: 100 }}
                />
              </Box>
              <TextField
                label="Label (optional)"
                size="small"
                placeholder="First iteration"
                value={cycleLabel}
                onChange={(e) => setCycleLabel(e.target.value)}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" onClick={handleAddCycle}>
                  Add
                </Button>
                <Button size="small" onClick={() => setShowCycleForm(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!label.trim()}
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
